import { ButtonContainer, DEButton } from "@repo/dynamicEditor/src/buttons";
import DynamicEditorContainer from "@repo/dynamicEditor/src/container";
import { traverseProperty } from "@repo/dynamicEditor/src/helpers/dynamicProperties";
import { useMappedSections } from "@repo/dynamicEditor/src/helpers/mappedSectionHook";
import { ComponentValidationResult } from "@repo/dynamicEditor/src/models/mappedComponent";
import { WebViewContainer } from "@repo/dynamicEditor/src/webView";
import { clone, downloadFile, log, MultiValidatorFn, styles } from "@repo/helpers";
import { keyPair, KeyPairArray, SerializedFile } from "@repo/models";
import { ButtonTypes, Row } from "@repo/ui";
import { MutableRefObject, PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";

import { getPortalComponents } from "../components";
import { MappedComponent, section } from "../components/portalDynamicComponent";
import { mapSection } from "../sectionMapping";
import { ITemplateData } from "../templateDataHook";
import style from "./style.module.scss";

//Consider the possibility of stepping away from templateData being a ref
//If doing await thing, need to consider the possibility that value hasn't changed
//If doing full queue, can roll back updateProperties to just multiple updateProperty calls

function DynamicTab<TData extends ITemplateData>(props: DTProps<TData>) {
	const { templateData, sections, dirty, submitButtonText, saveAndEmail, loaded } = props;
	const { updateData, editData, initialized } = props;
	const { validator, updateValues, save, postSave } = props;
	const [saving, setSaving] = useState(false);
	const getMappedSections = useCallback(
		function () {
			if (!sections) return [];
			return sections.map(mapSection({ ...props, ...templateData.current }));
		},
		[sections, dirty, loaded],
	);

	const { mappedSections, validationResults, completed } = useMappedSections(getMappedSections, [dirty], validator);

	const { updateProperty, updateProperties } = useUpdateQueue(
		initialized,
		templateData,
		updateValues,
		updateData,
		editData,
	);

	const onSave = useCallback(
		async function (email: boolean) {
			try {
				setSaving(true);
				await save(email);
			} catch (ex) {
				log(`Error Saving: ${ex}`, "error");
			} finally {
				setSaving(false);
				if (postSave) await postSave();
			}
		},
		[setSaving, save, postSave],
	);

	const getFile = useCallback(function (fileId: number) {
		downloadFile("/MVC/AgentPortal/GetRepDocument/" + fileId);
	}, []);

	const deleteFile = useCallback(
		async function (fileId: number, propId: string) {
			const files = traverseProperty(propId, templateData.current) as SerializedFile[];
			if (!files) return;
			const fileIndex = files.findIndex((f) => f.id === fileId);
			if (fileIndex === -1) return;
			const newFiles = [...files];
			const file = clone(files[fileIndex]);
			newFiles.splice(fileIndex, 1, file);
			file.deleted = !file.deleted;
			await updateProperty(propId, newFiles);
			//TODO
		},
		[updateProperty],
	);

	const valueChanged = useCallback(
		async function (value: any, id: string, parentPropertyName?: string, dontEdit = false) {
			props.keepAlive && props.keepAlive();
			await updateProperty(id, value, parentPropertyName, dontEdit);
		},
		[props.keepAlive, updateProperty],
	);

	return (
		<Row id={props.name} tag="section" className={style.container}>
			{props.children}
			<DynamicEditorContainer
				templateData={templateData.current}
				validationResults={validationResults}
				sections={mappedSections}
				validateOnSave
			>
				<WebViewContainer
					containerClass={styles(props.containerClass /** style.middleContainers */)}
					getCustomComponent={getPortalComponents({
						...props,
						...templateData.current,
					})}
					completed={completed}
					addItem={emptyItemFn}
					removeItem={emptyItemFn}
					valueChanged={valueChanged}
					deleteUploadedFile={deleteFile}
					getUploadedFile={getFile}
					valuesChanged={updateProperties}
					readOnly={props.entryDisabled || saving}
					customFunctions={props.customFunctions}
				>
					<ButtonContainer completed={completed} submitting={saving} submitError={props.submissionError}>
						<DEButton onClick={() => onSave(false)} types={[ButtonTypes.Primary]} mustBeComplete>
							{submitButtonText ?? "Save"}
						</DEButton>
						{saveAndEmail && (
							<DEButton onClick={() => onSave(true)} types={[ButtonTypes.Green]} mustBeComplete>
								{"Save and Email"}
							</DEButton>
						)}
						{props.cancel && <DEButton onClick={props.cancel}>Cancel</DEButton>}
					</ButtonContainer>
				</WebViewContainer>
			</DynamicEditorContainer>
		</Row>
	);
}

function emptyItemFn() {
	return () => null;
}

const defaultEditQueue = new KeyPairArray<EditRequest>();
function useUpdateQueue<TData>(
	initialized: boolean,
	data: MutableRefObject<TData>,
	changeFn: (id: string, value: any, newState: TData, parentComponent?: string) => Promise<EditResult<TData>>,
	updateFn: (s: TData) => void,
	editFn: (s: TData) => void,
) {
	const updateQueue = useRef(defaultEditQueue);
	const queueRecord = useRef([] as EditRequestRecord[]);
	const runningQueue = useRef(false);
	const lastQueueId = useRef(0);

	const [dirtyQueue, setDirtyQueue] = useState(0);
	useEffect(() => {
		if (runningQueue.current) return;
		const nextInQueue = updateQueue.current.data[0]?.id as number;
		if (nextInQueue) executeEdit(nextInQueue);
		else lastQueueId.current = 0;
	}, [dirtyQueue]);

	function runQueue() {
		if (runningQueue.current) return;
		if (!updateQueue.current.any()) return;
		setDirtyQueue(Math.random());
	}

	async function executeEdit(id: number) {
		runningQueue.current = true;
		const editRequest = updateQueue.current.getValue(id);
		if (!editRequest) {
			runningQueue.current = false;
			return;
		}

		await pushUpdate(editRequest).then(editRequest.resolve);
		updateQueue.current.removeId(id);
		runningQueue.current = false;
		runQueue();

		async function pushUpdate(editRequest: EditRequest) {
			//If dev, log record for tracking
			if (import.meta.env.DEV) queueRecord.current.push({ executeTime: new Date(), ...editRequest });

			const { properties, parentComponent, dontEdit } = editRequest;
			if (!initialized) return;
			let changed = false;
			//Shallow clone (just one level down) template data
			let newState = {} as TData;
			const keys = Object.keys(data.current);
			for (const key of keys) newState[key] = { ...data.current[key] };

			for (const property of properties) {
				const result = await changeFn(property.id as string, property.value, newState, parentComponent);
				changed = changed || result.changed;
				newState = result.newState;
			}
			data.current = newState;
			if (changed && !dontEdit) editFn(newState);
			else updateFn(newState);
		}
	}

	const updateProperties = useCallback(function (properties: keyPair[], parentComponent?: string, dontEdit = false) {
		const nextId = ++lastQueueId.current;
		const editRequest = {
			properties,
			parentComponent,
			dontEdit,
		} as EditRequest;
		const promise = new Promise<void>((resolve) => {
			editRequest.resolve = resolve;
		});
		updateQueue.current.setValue(nextId, editRequest);
		runQueue();
		return promise;
	}, []);

	const updateProperty = useCallback(
		function (id: string, value: any, parentComponent?: string, dontEdit = false) {
			return updateProperties([{ id, value }], parentComponent, dontEdit);
		},
		[updateProperties],
	);

	return { updateProperty, updateProperties };
}

export default DynamicTab;
interface EditRequest {
	properties: keyPair[];
	parentComponent?: string;
	dontEdit: boolean;
	resolve?: () => void;
}
interface EditRequestRecord extends EditRequest {
	executeTime: Date;
}
interface EditResult<TData> {
	changed: boolean;
	newState: TData;
}
export interface DTProps<TData extends ITemplateData> extends PropsWithChildren<any> {
	name: string;
	save: (email: boolean) => Promise<any>;
	postSave?: () => Promise<any>;
	cancel?: () => void;
	dirty: number;
	containerClass?: string;
	entryDisabled?: boolean;
	keepAlive?: () => void;
	submitButtonText?: string;
	saveAndEmail?: boolean;

	validator: (component: MappedComponent) => MultiValidatorFn<any, ComponentValidationResult>;
	initialized: boolean;
	sections: section[];
	templateData: MutableRefObject<TData>;
	updateValues?: (id: string, value: any, newState: TData, parentComponent?: string) => Promise<EditResult<TData>>;
	editData: (data: TData) => void;
	updateData: (data: TData) => void;
	customFunctions?: (name: string) => () => Promise<void>;
}
